import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { User } from '../models/User';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { UserService } from '../services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { SourceAccountService } from '../services/sourceAccountService.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AddUserModalComponent } from './add-user-modal/add-user-modal.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { Page } from '../models/Page';
import { PagedData } from '../models/PagedData';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatCheckboxModule,
    FormsModule,
    MatTableModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    RouterModule
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {
  private readonly _liveAnnouncer = inject(LiveAnnouncer);
  public dataSource = new MatTableDataSource<User>([]);
  public isLoading = false;
  public roles = ['Admin', 'Contributor'];
  public companiesList: string[] = [];
  public companies = new FormControl('');
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  public searchKeyword: string = '';
  public searchingKeywords: boolean = false;

  public displayedColumns: string[] = [
    'Name',
    'Initials',
    'Email',
    'Role',
    'Companies',
    'actions'
  ];

  public page: Page = {
    pageIndex: 0,
    pageSize: 10,
    length: 0,
    filter: undefined,
    orderBy: 'Name',
    orderDirection: 'asc',
    filterKeyword: undefined
  };

  constructor(
    private readonly usersService: UserService,
    public snackBar: MatSnackBar,
    public sourceAccountService: SourceAccountService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit() {
    this.getUsers(this.page);
    this.getCompanies();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  openAddUserModal(): void {
    const dialogRef = this.dialog.open(AddUserModalComponent, {
      width: '400px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshUserData();
      }
    });
  }

  onSearch() {
    this.searchingKeywords = true;
    this.page.filterKeyword = this.searchKeyword || '';
    this.getUsers(this.page);
  }

  clearSearch() {
    this.searchKeyword = '';
    this.page.filterKeyword = '';
    this.getUsers(this.page);
  }


  refreshUserData(): void {
    this.getUsers(this.page);
  }

  pageChanged(event: PageEvent) {
    this.isLoading = true;
    this.page.pageIndex = event.pageIndex;
    this.page.pageSize = event.pageSize;
    this.getUsers(this.page);
  }

  announceSortChange(sortState: Sort) {
    this.isLoading = true;
    this.page.orderBy = sortState.active;
    this.page.orderDirection = sortState.direction;
    this.getUsers(this.page);
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  getUsers(page: Page) {
    if (!this.searchingKeywords) {
      this.isLoading = true;
    }

    this.page.pageIndex = page.pageIndex;

    this.usersService.getUsers(page).subscribe({
      next: (pagedData: PagedData<User>) => {
        this.page.length = pagedData.data.length;
        this.searchingKeywords = false;
        this.page = pagedData.page;
        this.dataSource.data = pagedData.data;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.searchingKeywords = false;
        this.snackBar.open('Error fetching users', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }

  getCompanies() {
    this.sourceAccountService.getCompanies().subscribe({
      next: (data: string[]) => {
        this.companiesList = data;
        this.companiesList.push('fh_aut.');
      },
      error: () => {
        this.snackBar.open('Error fetching companies', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }

  discardChanges(element: User): void {
    element.role = element.originalRole ?? null;
    element.companies = element.originalCompanies ?? null;
    element.isEditing = false;
    element.isSaving = false;
  }

  editElement(element: User): void {
    element.originalRole = element.role ?? null;
    element.originalCompanies = element.companies ? [...element.companies] : null;
    element.isEditing = true;
  }

  saveUser(element: User): void {
    element.isSaving = true;
    this.usersService.updateUser(element).subscribe({
      next: () => {
        element.isEditing = false;
        element.isSaving = false;

        this.snackBar.open('Update successful', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      },
      error: () => {
        this.snackBar.open('Update failed. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });

        this.discardChanges(element);
      }
    });
  }

  deleteUser(user: User): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '300px',
      data: {
        title: 'Delete User',
        message: `Are you sure you want to delete the user "${user.name}"?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.usersService.deleteUser(user).subscribe({
          next: (response) => {
            if (response) {
              this.snackBar.open('User successfully deleted', 'Close', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
              this.refreshUserData();
            }
          },
          error: () => {
            this.snackBar.open('Delete user failed. Please try again.', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          }
        });
      }
    });
  }
}
